import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Row,
  Col,
  Divider,
  Tabs,
  Select,
  message
} from 'antd';
import { UserOutlined, LockOutlined, MobileOutlined, SafetyOutlined } from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import {
  countrySelectOptions,
  DEFAULT_COUNTRY,
  LOOSE_PHONE_PATTERN,
  detectDefaultCountry,
} from '../utils/countryCodes';

const { Title, Paragraph } = Typography;

const Login = () => {
  const [pwdForm] = Form.useForm();
  const [phoneForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, sendPhoneCode, phoneLogin } = useAuth();
  const { t, i18n } = useTranslation('auth');

  // 回跳目标只取自 location.state（站内导航带来，URL 不可控），保留查询串（如带筛选的列表页）
  const fromLoc = location.state?.from;
  const from = fromLoc?.pathname ? fromLoc.pathname + (fromLoc.search || '') : '/dashboard';

  // 被动登出的原因（services/api forceReauth 带来）
  useEffect(() => {
    if (new URLSearchParams(location.search).get('reason') === 'revoked') {
      message.warning(t('login.revoked'));
    }
  }, [location.search, t]);

  // 验证码倒计时
  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  // 按访客所在地探测默认手机区号（CF-IPCountry → navigator.language → 兜底），
  // 挂载即探测、先于用户交互，所以直接覆盖 initialValue；探测失败会安全回退，不影响登录。
  useEffect(() => {
    let active = true;
    detectDefaultCountry().then((cc) => {
      if (active && cc) phoneForm.setFieldValue('country_code', cc);
    });
    return () => { active = false; };
  }, [phoneForm]);

  const handlePasswordLogin = async (values) => {
    setLoading(true);
    try {
      const result = await login(values);
      if (result.success) navigate(from, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    try {
      await phoneForm.validateFields(['phone']);
    } catch {
      return;
    }
    const phone = phoneForm.getFieldValue('phone');
    const countryCode = phoneForm.getFieldValue('country_code');
    setCodeLoading(true);
    try {
      const res = await sendPhoneCode(phone, countryCode);
      if (res.success) {
        setCountdown(60);
        if (res.devCode) {
          // 开发模式：回显并自动填入，便于联调
          message.info(t('login.devCode', { code: res.devCode }));
          phoneForm.setFieldsValue({ code: res.devCode });
        } else {
          message.success(t('login.codeSent'));
        }
      }
    } finally {
      setCodeLoading(false);
    }
  };

  const handlePhoneLogin = async (values) => {
    setLoading(true);
    try {
      const result = await phoneLogin(values);
      if (result.success) navigate(from, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const passwordTab = (
    <Form form={pwdForm} name="login" onFinish={handlePasswordLogin} layout="vertical" size="large">
      <Form.Item
        name="login"
        rules={[{ required: true, message: t('login.accountRequired') }]}
      >
        <Input prefix={<UserOutlined />} placeholder={t('login.account')} />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          { required: true, message: t('login.passwordRequired') },
          { min: 6, message: t('login.passwordMin') }
        ]}
      >
        <Input.Password prefix={<LockOutlined />} placeholder={t('login.password')} />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} block size="large">
          {t('login.submit')}
        </Button>
      </Form.Item>
    </Form>
  );

  const phoneTab = (
    <Form form={phoneForm} name="phoneLogin" onFinish={handlePhoneLogin} layout="vertical" size="large">
      <Form.Item
        name="phone"
        rules={[
          { required: true, message: t('login.phoneRequired') },
          { pattern: LOOSE_PHONE_PATTERN, message: t('login.phoneInvalid') }
        ]}
      >
        <Input
          prefix={<MobileOutlined />}
          placeholder={t('login.phonePlaceholder')}
          addonBefore={
            <Form.Item name="country_code" noStyle initialValue={DEFAULT_COUNTRY}>
              <Select
                style={{ width: 130 }}
                options={countrySelectOptions(i18n.language)}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          }
        />
      </Form.Item>
      <Form.Item
        name="code"
        rules={[
          { required: true, message: t('login.codeRequired') },
          { len: 6, message: t('login.codeLen') }
        ]}
      >
        <Input
          prefix={<SafetyOutlined />}
          placeholder={t('login.code')}
          maxLength={6}
          addonAfter={
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              loading={codeLoading}
              disabled={countdown > 0}
              onClick={handleSendCode}
            >
              {countdown > 0 ? t('login.resendIn', { s: countdown }) : t('login.sendCode')}
            </Button>
          }
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} block size="large">
          {t('login.submitPhone')}
        </Button>
      </Form.Item>
      <Paragraph type="secondary" style={{ fontSize: 12, margin: 0, textAlign: 'center' }}>
        {t('login.autoRegister')}
      </Paragraph>
    </Form>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Row justify="center" style={{ width: '100%', maxWidth: '400px' }}>
        <Col xs={24}>
          <Card style={{ borderRadius: '12px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Title level={2} style={{ marginBottom: '8px' }}>{t('login.title')}</Title>
              <Paragraph style={{ color: '#666', margin: 0 }}>{t('login.subtitle')}</Paragraph>
            </div>

            <Tabs
              centered
              items={[
                { key: 'phone', label: t('login.tabCode'), children: phoneTab },
                { key: 'password', label: t('login.tabPassword'), children: passwordTab }
              ]}
            />

            <Divider>{t('login.or')}</Divider>

            <div style={{ textAlign: 'center' }}>
              <Paragraph style={{ margin: 0 }}>
                {t('login.noAccount')}{' '}
                <Link to="/register" style={{ fontWeight: 'bold' }}>{t('login.registerNow')}</Link>
              </Paragraph>
              <Paragraph style={{ margin: '8px 0 0 0' }}>
                <Link to="/forgot-password">{t('login.forgot')}</Link>
              </Paragraph>
              <Paragraph style={{ margin: '8px 0 0 0' }}>
                <Link to="/admin/login">{t('login.adminLogin')}</Link>
              </Paragraph>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Login;
